import { API_URL, GetStarted_API_TOKEN, ENV } from '../../config';
import { HandlePublishDate } from '../../utils/PubulishDateFilter';
import { IChannels, ILibraries, Plan } from '../content/types';
import { filterCustomerTypeDisplay } from './filterCustomerTypeDisplay';
import { applyPreproductionSettings } from '../../utils/PreproductionSettings';
import { SOURCE_URL } from '../../config';

interface ComboPack {
  id: number;
  attributes: {
    name: string;
    description: string;
    packImage: {
      data: {
        attributes: {
          url: string;
        };
      }[];
    };
    // Add other fields as necessary
  };
}

interface GetStartedAttributes {
  stb_detail_id: string;
  includes: any;
  heading: string;
  bottom: string;
  plans: {
    data: Plan[];
  };
  Filter?: {
    channels: {
      data: IChannels[];
    };
    libraries: {
      data: ILibraries[];
    };
    id: number;
  };
  SEO: SEO;
  GA: GA[];
  // Add other fields as necessary
}

interface GetStartedItem {
  id: number;
  attributes: GetStartedAttributes;
}

interface ApiResponse<T> {
  data: T[];
  error?: {
    status: number;
    name: string;
    message: string;
    details: any;
  };
}

interface SEO {
  Title: string;
  MetaDescription: string;
  MetaKeywords: string;
  MetaImage: string;
}

interface GA {
  Remarks: string;
  Location: string;
  Code: string;
}

interface GetStartedResponse {
  data: GetStartedItem;
  error?: {
    status: number;
    name: string;
    message: string;
    details: any;
  };
}

interface CombinedResponse {
  plans: Plan[];
  combopacks: GetStartedItem[];
  getStarted: GetStartedItem | null;
  SEO: SEO | null;
  GA: GA[] | null;
}

interface getPackInfoByIdResponse {
  combopack: GetStartedItem[];
}

interface getAvaliableChannelPacksResponse{
  packs: any;
  SEO: SEO | null;
  GA: GA[] | null;
}

const fetchWithAuth = async (url: string, lang: string) => {
  url = applyPreproductionSettings(url);
  const isZhRequest = lang.includes('zh');
  if (lang.includes('zh')) {
    url += url.includes('?') ? '&locale=zh-Hant-HK&fallbackLocale=false' : '?locale=zh-Hant-HK&fallbackLocale=false';
  }
  let response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${GetStarted_API_TOKEN}`,
    },
  });

  if (!response.ok && isZhRequest && response.status === 404) {
    const fallbackUrl = url.replace(/([&?])fallbackLocale=false(&?)/, (_, prefix, suffix) => {
      if (suffix) return prefix;
      return '';
    }).replace(/[?&]$/, '');

    response = await fetch(fallbackUrl, {
      headers: {
        Authorization: `Bearer ${GetStarted_API_TOKEN}`,
      },
    });
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

const fetchData = async (lang: string, ott: boolean, endpoint: string, custType?: string): Promise<CombinedResponse | null> => {
  try {
    const platformParams = ott
      ? 'filters[PriceOptions][$notNull]=true'
      : 'filters[PriceOptionsSTB][$notNull]=true';
    const getStartedParams = ott 
      ? 'Filter.channels.icon,Filter.libraries.icon,plans.PriceOptions,' 
        + 'plans.plan_img,' 
        + 'plans.includes,plans.packs,plans.localizations,plans.featureImage,plans.CustomerTypeDisplay'
      : 'Filter.channels.icon,Filter.libraries.icon,' 
        + 'plans.PriceOptionsSTB,plans.plan_img,' 
        + 'plans.includes,plans.packs,plans.localizations,plans.featureImage,plans.CustomerTypeDisplay'

    const [plansResponse, combopacksResponse, getStartedResponse, seoGaResponse] = await Promise.all([
      fetchWithAuth(`${API_URL}/plans?${platformParams}&populate=*&pagination[limit]=-1`, lang),
      fetchWithAuth(`${API_URL}/combo-packs?populate=*&pagination[limit]=-1`, lang),
      fetchWithAuth(`${API_URL}/${endpoint}?populate=${getStartedParams}&pagination[limit]=-1`, lang),
      fetchWithAuth(`${API_URL}/${endpoint}?populate=SEO.MetaImage,GA&pagination[limit]=-1`, lang),
    ]);
    if(seoGaResponse?.data?.attributes?.SEO?.MetaImage)
      seoGaResponse.data.attributes.SEO.MetaImage = SOURCE_URL + seoGaResponse.data.attributes.SEO.MetaImage?.data?.attributes?.url;
    const combinedResponse: CombinedResponse = {
      plans: filterCustomerTypeDisplay(HandlePublishDate(plansResponse.data), custType),
      combopacks: HandlePublishDate(combopacksResponse.data),
      getStarted: HandlePublishDate(getStartedResponse.data),
      SEO: seoGaResponse.data.attributes.SEO || null,
      GA: seoGaResponse.data.attributes.GA || null,
    };

    console.log('Combined API Response:', combinedResponse); // Log the combined API response
    return combinedResponse;
  } catch (error) {
    console.error('Error fetching data.', error);
    return null; // Return null in case of an error
  }
};

let currentLang = '';

export const refetchDataOnLanguageChange = async (lang: string, ott: boolean, endpoint: string, custType?: string): Promise<CombinedResponse | null> => {
  if (lang !== currentLang) {
    currentLang = lang;
    return fetchData(lang, ott, endpoint, custType);
  }
  return null;
};

export const fetchGetStarted = async (lang: string, custType?: string): Promise<CombinedResponse | null> => {
  const response = await fetchData(lang, true, 'get-started', custType);
  if (response) {
    response.plans = filterCustomerTypeDisplay(response.plans, custType);
  }
  return response;
};

export const fetchGetStartedBox = async (lang: string, custType?: string): Promise<CombinedResponse | null> => {
  const response = await fetchData(lang, false, 'get-started-box', custType);
  if (response) {
    response.plans = filterCustomerTypeDisplay(response.plans, custType);
  }
  return response;
};

export const fetchBoxDetailsPlan = async (lang: string): Promise<GetStartedAttributes | null> => {

  try {
    const platformParams ='filters[PriceOptionsSTB][$notNull]=true';

    const plansResponse = await fetchWithAuth(`${API_URL}/plans?${platformParams}&populate=*&pagination[limit]=-1`, lang);

    const plans: GetStartedAttributes = {
      plans: HandlePublishDate(plansResponse.data),
    };

    console.log('Combined API Response:', plans); // Log the combined API response
    return plans;
  } catch (error) {
    console.error('Error fetching data.', error);
    return null; // Return null in case of an error
  }

};

export const getPackInfoById = async (id: string, lang: string): Promise<getPackInfoByIdResponse | null> => {
  try {
    const combopacksResponse = await fetchWithAuth(
      `${API_URL}/combo-packs/${id}?populate[onDemandNumber][populate]=icon&populate[channelsNumber][populate]=icon&populate[packImage]=packImage&populate[packSquareImage]=packSquareImage`,
      lang
    );

    const packResponse: getPackInfoByIdResponse = {
      combopack: HandlePublishDate(combopacksResponse.data),
    };

    console.log('getPackInfoById API Response:', packResponse); // Log the combined API response
    return packResponse;
  } catch (error) {
    console.error('Error fetching data.', error);
    return null; // Return null in case of an error
  }
};

export const getAvaliableChannelPacks = async (lang: string): Promise<getAvaliableChannelPacksResponse | null> => {
  try {
    const avaliableChannelPacksResponse = await fetchWithAuth(
      `${API_URL}/available-channel-pack?populate=Adult.packImage,Entertainment.packImage,FunnLearn.packImage,Infotainment.packImage,ForeignLanguage.packImage,Movie.packImage,Sports.packImage,SEO.MetaImage,GA`,
      lang
    );
    if(avaliableChannelPacksResponse?.data?.attributes?.SEO?.MetaImage)
      avaliableChannelPacksResponse.data.attributes.SEO.MetaImage = SOURCE_URL + avaliableChannelPacksResponse.data.attributes.SEO.MetaImage?.data?.attributes?.url;
    const packResponse: getAvaliableChannelPacksResponse = {
      packs: HandlePublishDate(avaliableChannelPacksResponse.data.attributes),
      SEO: avaliableChannelPacksResponse.data.attributes.SEO || null,
      GA: avaliableChannelPacksResponse.data.attributes.GA || null,
    };

    console.log('getAvaliableChannelPacks API Response:', packResponse); // Log the combined API response
    return packResponse;
  } catch (error) {
    console.error('Error fetching data.', error);
    return null; // Return null in case of an error
  }
};